/**
 * Governed cinema chain (circuit) recognition.
 * -----------------------------------------------------------------------------
 * Theatrical footprint sources are inconsistent: some carry an explicit
 * Circuit/Chain/Exhibitor column, many (including the Never Stop Chasing
 * theater footprint) carry only the full theater name. Before this module the
 * chain was read exclusively from the column, so a name-only source resolved
 * every venue to "Independent / Unknown" — 62 AMC locations included.
 *
 * The contract:
 *   - the ORIGINAL theater name is never rewritten; the chain is a SEPARATE,
 *     derived, canonical field;
 *   - the chain is resolved from a governed, ordered, first-match-wins rule
 *     table — never from "the first word of the name";
 *   - sub-brands roll up to their parent chain (AMC CLASSIC -> AMC);
 *   - an explicit exhibitor column stays authoritative even when it names a
 *     circuit this table does not know yet (that is real exhibitor data, not an
 *     unrecognised venue);
 *   - a venue that matches nothing is honestly "Independent / Other" — a named
 *     state, not a silent blank.
 *
 * Adding a chain is a data edit to CINEMA_CHAIN_RULES. No consumer changes.
 *
 * This ES-module copy is byte-identical between the Cortex (client) and
 * Quantum (superadmin) portals, and behaviour-identical to the backend mirror
 * at backend/utils/cinemaChainRecognition.js. All three are pinned by
 * backend/tests/cinemaChainRecognitionFrontendParity.test.js.
 */

export const INDEPENDENT_CHAIN_KEY = "INDEPENDENT";
export const INDEPENDENT_CHAIN_LABEL = "Independent / Other";
export const UNSPECIFIED_CHAIN_KEY = "UNSPECIFIED";
export const UNSPECIFIED_CHAIN_LABEL = "Unspecified";

// Values that mean "no exhibitor was supplied" rather than naming one.
const BLANK_CIRCUIT_VALUES = new Set([
  "",
  "N A",
  "NA",
  "NONE",
  "NULL",
  "UNKNOWN",
  "UNSPECIFIED",
  "TBD",
  "OTHER",
]);

// Circuit columns are frequently region-prefixed ("US Regal", "CA Cineplex").
const REGION_PREFIX_PATTERN = /^(?:US|USA|CA|CAN|NA)\s+/;

/**
 * Comparison form used by every rule matcher.
 *
 * Curly apostrophes, ampersands, hyphens, accents and doubled whitespace must
 * never change which chain a venue belongs to, so all of them are folded away
 * before matching. The result is UPPERCASE, punctuation-free and single-spaced:
 *   "Mary Pickford Is D’Place"          -> "MARY PICKFORD IS DPLACE"
 *   "Malco Ridgeway Cinema Grill & Bar" -> "MALCO RIDGEWAY CINEMA GRILL AND BAR"
 *   "UltraStar Mission Valley - Hazard" -> "ULTRASTAR MISSION VALLEY HAZARD"
 */
export function normalizeChainMatchText(value) {
  return String(value === null || value === undefined ? "" : value)
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[\u2018\u2019\u02bc'`\u00b4]/g, "")
    .replace(/&/g, " AND ")
    .replace(/[^0-9A-Za-z]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toUpperCase();
}

/**
 * Governed chain table. Order matters: the first rule whose matcher hits wins,
 * so more specific brands must precede the generic families they belong to.
 * Matchers run against normalizeChainMatchText() output — write them without
 * punctuation and in uppercase.
 */
export const CINEMA_CHAIN_RULES = Object.freeze([
  {
    key: "AMC",
    label: "AMC",
    // AMC CLASSIC / AMC DINE-IN are AMC banners, not separate circuits, and
    // must roll up rather than surface as unrecognised chains.
    matchers: [/^AMC\b/, /^AMC (?:CLASSIC|DINE IN|DOLBY|ONLY)\b/, /\bAMC THEAT(?:RE|ER)S?\b/],
  },
  {
    key: "HARKINS",
    label: "Harkins Theatres",
    matchers: [/^HARKINS\b/, /\bHARKINS THEAT(?:RE|ER)S?\b/],
  },
  {
    key: "ULTRASTAR",
    label: "UltraStar",
    matchers: [/^ULTRASTAR\b/, /^ULTRA STAR\b/, /\bULTRASTAR CINEMAS?\b/],
  },
  {
    key: "MALCO",
    label: "Malco",
    matchers: [/^MALCO\b/, /\bMALCO THEAT(?:RE|ER)S?\b/],
  },
  {
    key: "REGAL",
    label: "Regal",
    matchers: [/^REGAL\b/, /\bREGAL CINEMAS?\b/, /\bREGAL ENTERTAINMENT\b/],
  },
  {
    // Cinemark family brands all report as one circuit.
    key: "CINEMARK",
    label: "Cinemark",
    matchers: [
      /^CINEMARK\b/,
      /\bCINEMARK\b/,
      /\bCENTURY (?:THEAT(?:RE|ER)S?|\d)/,
      /\bCINE ?ARTS\b/,
      /\bTINSELTOWN\b/,
      /\bRAVE CINEMAS?\b/,
    ],
  },
  {
    key: "MARCUS",
    label: "Marcus Theatres",
    matchers: [/^MARCUS\b/, /\bMARCUS THEAT(?:RE|ER)S?\b/, /\bMOVIE TAVERN\b/],
  },
  {
    key: "CINEPLEX",
    label: "Cineplex",
    matchers: [
      /^CINEPLEX\b/,
      /\bCINEPLEX\b/,
      /\bSCOTIABANK THEAT(?:RE|ER)S?\b/,
      /\bSILVERCITY\b/,
      /\bGALAXY CINEMAS\b/,
    ],
  },
  {
    key: "BANDB",
    label: "B&B Theatres",
    // "&" normalizes to " AND ", so "B&B" arrives as "B AND B".
    matchers: [/^B AND B\b/, /\bB AND B THEAT(?:RE|ER)S?\b/],
  },
  {
    key: "BRENDEN",
    label: "Brenden Theatres",
    matchers: [/^BRENDEN\b/, /\bBRENDEN THEAT(?:RE|ER)S?\b/],
  },
  {
    key: "ALAMO",
    label: "Alamo Drafthouse",
    matchers: [/^ALAMO DRAFTHOUSE\b/, /\bALAMO DRAFTHOUSE\b/],
  },
  {
    key: "LANDMARK",
    label: "Landmark Theatres",
    matchers: [/^LANDMARK\b/, /\bLANDMARK THEAT(?:RE|ER)S?\b/],
  },
  {
    key: "STUDIO_MOVIE_GRILL",
    label: "Studio Movie Grill",
    matchers: [/^STUDIO MOVIE GRILL\b/],
  },
  {
    key: "IPIC",
    label: "iPic",
    matchers: [/^IPIC\b/],
  },
  {
    key: "BOWTIE",
    label: "Bow Tie Cinemas",
    matchers: [/^BOW TIE\b/],
  },
  {
    key: "EMAGINE",
    label: "Emagine Entertainment",
    matchers: [/^EMAGINE\b/],
  },
  {
    key: "MEGAPLEX",
    label: "Megaplex Theatres",
    matchers: [/^MEGAPLEX\b/],
  },
  {
    key: "SANTIKOS",
    label: "Santikos Entertainment",
    matchers: [/^SANTIKOS\b/],
  },
  {
    key: "ANGELIKA",
    label: "Angelika Film Center",
    matchers: [/^ANGELIKA\b/],
  },
  {
    key: "LAEMMLE",
    label: "Laemmle Theatres",
    matchers: [/^LAEMMLE\b/],
  },
]);

function isBlankCircuitValue(comparison) {
  return !comparison || BLANK_CIRCUIT_VALUES.has(comparison);
}

function matchGovernedChainRule(comparison) {
  if (!comparison) return null;
  const withoutRegionPrefix = comparison.replace(REGION_PREFIX_PATTERN, "");
  for (const rule of CINEMA_CHAIN_RULES) {
    for (const matcher of rule.matchers) {
      if (matcher.test(comparison) || matcher.test(withoutRegionPrefix)) {
        return rule;
      }
    }
  }
  return null;
}

/**
 * Resolve the canonical chain for one venue.
 *
 * @param {object} input
 * @param {string} input.theaterName  Full venue name, preserved verbatim by callers.
 * @param {string} [input.circuit]    Explicit Circuit/Chain/Exhibitor column value, if any.
 * @param {boolean} [input.allowIndependentFallback=true]
 *        When false an unmatched, column-less venue resolves to "Unspecified"
 *        instead of "Independent / Other". Used by the central location roster,
 *        where absence of a name is genuinely "we hold no exhibitor evidence".
 * @returns {{key: string, label: string, source: string, rawValue: string}}
 *          source: "circuit_column" | "circuit_column_unmatched" | "theater_name" | "unmatched"
 */
export function resolveCinemaChain(input = {}) {
  const rawCircuit = String(input.circuit === null || input.circuit === undefined ? "" : input.circuit).trim();
  const rawName = String(
    input.theaterName === null || input.theaterName === undefined ? "" : input.theaterName
  ).trim();
  const allowIndependentFallback = input.allowIndependentFallback !== false;

  const circuitComparison = normalizeChainMatchText(rawCircuit);
  if (!isBlankCircuitValue(circuitComparison)) {
    const rule = matchGovernedChainRule(circuitComparison);
    if (rule) {
      return { key: rule.key, label: rule.label, source: "circuit_column", rawValue: rawCircuit };
    }
    // A named exhibitor this table does not know yet is still evidence. Keep it
    // verbatim rather than demoting real circuit data to "Independent / Other".
    return {
      key: circuitComparison.replace(/\s+/g, "_"),
      label: rawCircuit,
      source: "circuit_column_unmatched",
      rawValue: rawCircuit,
    };
  }

  const nameComparison = normalizeChainMatchText(rawName);
  const nameRule = matchGovernedChainRule(nameComparison);
  if (nameRule) {
    return { key: nameRule.key, label: nameRule.label, source: "theater_name", rawValue: rawName };
  }

  // No exhibitor column AND no venue name is an absence of evidence, not an
  // independent venue.
  if (!rawName) {
    return { key: UNSPECIFIED_CHAIN_KEY, label: UNSPECIFIED_CHAIN_LABEL, source: "unmatched", rawValue: "" };
  }

  return {
    key: allowIndependentFallback ? INDEPENDENT_CHAIN_KEY : UNSPECIFIED_CHAIN_KEY,
    label: allowIndependentFallback ? INDEPENDENT_CHAIN_LABEL : UNSPECIFIED_CHAIN_LABEL,
    source: "unmatched",
    rawValue: rawName,
  };
}

/** Convenience: the canonical chain LABEL only. */
export function resolveCinemaChainLabel(input = {}) {
  return resolveCinemaChain(input).label;
}

/** Ordered { label, count } breakdown for chain summaries and filters. */
export function buildCinemaChainDistribution(venues = []) {
  const counts = new Map();
  (Array.isArray(venues) ? venues : []).forEach((venue) => {
    const resolved = resolveCinemaChain(venue || {});
    const existing = counts.get(resolved.key);
    if (existing) existing.count += 1;
    else counts.set(resolved.key, { key: resolved.key, label: resolved.label, count: 1 });
  });
  return Array.from(counts.values()).sort(
    (a, b) => b.count - a.count || a.label.localeCompare(b.label)
  );
}
